import { inputToFloat, MoneyFormatCurrencyStyle } from "utils/calcs";
import { ApplicationSummary } from "../types";
import Card from "./Card";

// The API returns amounts formatted, e.g. "14,000".
const toNumber = (value?: string | null) => {
  const number = inputToFloat(value);
  return typeof number === "number" && !Number.isNaN(number)
    ? number
    : undefined;
};

const formatMoney = (value?: number) =>
  value === undefined ? undefined : MoneyFormatCurrencyStyle.format(value);

const plural = (count: number, unit: string) =>
  `${count} ${unit}${count === 1 ? "" : "s"}`;

// 14 → "1 year 2 months", 120 → "10 years", 9 → "9 months".
const formatTerm = (totalMonths: number) => {
  const years = Math.floor(totalMonths / 12);
  const months = totalMonths % 12;
  return [years && plural(years, "year"), months && plural(months, "month")]
    .filter(Boolean)
    .join(" ");
};

interface LoanSummaryProps {
  application: ApplicationSummary;
}

const LoanSummary: React.FC<LoanSummaryProps> = ({ application }) => {
  const { loan, broker } = application;
  const loanAmount = toNumber(loan?.loanAmount);
  const brokerFee = toNumber(broker?.feeAmount);

  const rows = [
    { label: "Purpose", value: loan?.purposeOfLoan },
    {
      label: "Broker fee",
      value:
        brokerFee === undefined
          ? undefined
          : `${formatMoney(brokerFee)}${
              broker?.brokerFeeAddedToLoan ? ", added to loan" : ""
            }`,
    },
  ].filter((row) => row.value);

  return (
    <Card title="Loan summary">
      <div className="p-6">
        {loanAmount !== undefined && (
          <p className="flex flex-wrap items-baseline gap-x-2 pb-4">
            <span className="text-3xl font-medium tracking-tight text-brand-copy-primary">
              {formatMoney(loanAmount)}
            </span>
            {loan?.loanTermMonths && (
              <span className="text-sm text-muted-foreground">
                over {formatTerm(loan.loanTermMonths)}
              </span>
            )}
          </p>
        )}
        <dl className="divide-y border-t text-sm">
          {rows.map(({ label, value }) => (
            <div key={label} className="flex justify-between gap-4 py-3">
              <dt className="text-muted-foreground">{label}</dt>
              <dd className="text-right font-medium text-brand-copy-primary">
                {value}
              </dd>
            </div>
          ))}
        </dl>
      </div>
    </Card>
  );
};

export default LoanSummary;
